import { Component, OnDestroy, OnInit, Input } from '@angular/core';
import { CommonModule } from '@angular/common';

import { AlertController, IonButton, IonCol, IonGrid, IonIcon, IonRow } from '@ionic/angular/standalone';

import { addIcons } from 'ionicons';
import { pauseCircleOutline, checkmarkSharp, colorWandSharp } from 'ionicons/icons';
import { TranslateService } from '@ngx-translate/core';

import { Subscription, firstValueFrom } from 'rxjs';
import { DateTime } from 'luxon';

import { LeaderboardService } from 'projects/tools/src/lib/shared-module/leaderboard/leaderboard.service';
import { TranslateModule } from '@ngx-translate/core';
import { TimeFromMSPipe } from 'projects/tools/src/lib/shared-module/helpers/time-from-ms.pipe';
import { BadwordsNicknamePipe } from 'projects//tools/src/lib/shared-module/leaderboard/badwords-nickname.pipe';
import { TooltipModule } from 'ng2-tooltip-directive-major-angular-updates';
import { PuzzleFrameworkService } from '../../puzzle-framework-module/puzzle-framework.service';
import { PuzzleKeyboardService } from '../puzzle-softkeyboard/puzzle-keyboard.service';

@Component({
  selector: 'lib-app-leaderboard-tabs',
  templateUrl: './leaderboard-tabs.component.html',
  styleUrls: ['./leaderboard-tabs.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    TooltipModule,
    BadwordsNicknamePipe,
    IonButton,
    IonCol,
    IonIcon,
    IonGrid,
    IonRow,
    TimeFromMSPipe,
  ],
})
export class LeaderboardTabsComponent implements OnInit, OnDestroy {
  @Input() withMode: string;
  @Input() withMessage: boolean;

  private lifecycleSubscriptions: Subscription[];

  public smallProgress = false;
  public currentLang: string;

  public leaderboardTimeLocal = '';
  public leaderboardDateLocal = '';

  public leaderboardEntries: { [mode: string]: any[] } = {};

  public topN = 100;

  public mode = 'topn_relaxed';

  public strictRules: boolean;

  constructor(
    private translate: TranslateService,
    public leaderboard: LeaderboardService,
    public puzzleFramework: PuzzleFrameworkService,
    private alertController: AlertController,
    private keyboardService: PuzzleKeyboardService
  ) {
    addIcons({
      pauseCircleOutline,
      checkmarkSharp,
      colorWandSharp,
      spellcheck: 'svg/spellcheck.svg',
    });
  }

  ngOnInit() {
    if (this.withMode) {
      this.mode = this.withMode;
    } else if (this.leaderboard.leaderboardAssociation?.wantLeaderboardRules) {
      this.mode = 'topn_strict';
    } else {
      this.mode = 'topn_relaxed';
    }
    this.currentLang = this.translate.currentLang.indexOf('de-') === 0 ? 'de-DE' : 'en-US';

    this.smallProgress = true;

    this.lifecycleSubscriptions = [
      this.leaderboard.leaderboardContext$.subscribe((leaderboardContext) => {
        this.updateLeaderboardEntries(leaderboardContext, 'my_context');
        if (leaderboardContext) {
          this.strictRules = leaderboardContext.entry.strictRules;
          if (!this.withMode) {
            this.mode = 'my_context';
          }
        }
      }),
    ];

    this.leaderboard.getLeaderboardPage(0, this.topN, true).then((leaderboardContext) => {
      this.updateLeaderboardEntries(leaderboardContext, 'topn_strict');
    });

    this.leaderboard.getLeaderboardPage(0, this.topN, false).then((leaderboardContext) => {
      this.updateLeaderboardEntries(leaderboardContext, 'topn_relaxed');
    });

    this.leaderboard.updateLeaderboardAssociation().then((_) => {});
  }

  ngOnDestroy(): void {
    this.lifecycleSubscriptions.forEach(
      (subscription) => subscription && subscription.unsubscribe && subscription.unsubscribe()
    );
  }

  updateLeaderboardEntries(leaderboardContext, mode) {
    if (!leaderboardContext) {
      return;
    }

    if (leaderboardContext.ranking) {
      const ranking = leaderboardContext.ranking;
      const leaderboardEntries = [];
      for (const key of Object.keys(ranking)) {
        const rank = parseInt(key, 10);
        leaderboardEntries[rank - 1] = ranking[key];
        leaderboardEntries[rank - 1]['rank'] = rank;
      }
      const leaderboardEntriesWithGaps = [];
      let lastLeaderboardEntry;
      for (const entry of leaderboardEntries) {
        if (entry) {
          if (lastLeaderboardEntry && lastLeaderboardEntry.rank + 1 < entry.rank) {
            leaderboardEntriesWithGaps.push('gap');
          }
          leaderboardEntriesWithGaps.push(entry);
          lastLeaderboardEntry = entry;
        }
      }
      this.leaderboardEntries[mode] = leaderboardEntriesWithGaps;
      this.smallProgress = false;
    }

    if (leaderboardContext.entry) {
      this.leaderboardTimeLocal = DateTime.fromRFC2822(leaderboardContext.entry.submittedAt)
        .setLocale(this.currentLang)
        .toLocaleString({ hour: '2-digit', minute: '2-digit', hourCycle: 'h23', second: '2-digit' });
      this.leaderboardDateLocal = DateTime.fromRFC2822(leaderboardContext.entry.submittedAt)
        .setLocale(this.currentLang)
        .toLocaleString(DateTime.DATE_FULL);
    }
  }

  public changeMode(mode: string) {
    this.mode = mode;
  }

  public async reportNickname(entry, event) {
    event.stopPropagation();

    // Disable keyboard interception
    this.keyboardService.disable();

    // Create and show the confirmation alert
    const alertTitle = await firstValueFrom(this.translate.get('PUZZLE.leaderboard.report.alert.title'));
    const alertMessage = await firstValueFrom(this.translate.get('PUZZLE.leaderboard.report.alert.message'));
    const alertButtonCancel = await firstValueFrom(this.translate.get('PUZZLE.leaderboard.report.alert.button_cancel'));
    const alertButtonConfirm = await firstValueFrom(
      this.translate.get('PUZZLE.leaderboard.report.alert.button_confirm')
    );
    const alertReasonLabel = await firstValueFrom(this.translate.get('PUZZLE.leaderboard.report.alert.reason_label'));
    const alertReasonPlaceholder = await firstValueFrom(
      this.translate.get('PUZZLE.leaderboard.report.alert.reason_placeholder')
    );
    const alertConfirmation1Label = await firstValueFrom(
      this.translate.get('PUZZLE.leaderboard.report.alert.alert_confirmation_1_Label')
    );
    const alertConfirmation2Label = await firstValueFrom(
      this.translate.get('PUZZLE.leaderboard.report.alert.alert_confirmation_2_Label')
    );
    const alertConfirmationTitle = await firstValueFrom(
      this.translate.get('PUZZLE.leaderboard.report.alert.alert_confirmation_title')
    );
    const alertConfirmationRequired = await firstValueFrom(
      this.translate.get('PUZZLE.leaderboard.report.alert.alert_confirmation_required')
    );

    const alert = await this.alertController.create({
      header: alertTitle,
      message: alertMessage,
      inputs: [
        {
          name: 'report_reason',
          type: 'text',
          placeholder: alertReasonPlaceholder,
          label: alertReasonLabel,
          attributes: {
            required: true,
          },
          cssClass: 'required',
        },
        {
          name: 'confirmation1',
          type: 'textarea',
          placeholder: alertConfirmation1Label,
        },
        // {
        //   name: 'confirmation2',
        //   type: 'textarea',
        //   placeholder: alertConfirmation2Label,
        // },
      ],
      buttons: [
        {
          text: alertButtonCancel,
          role: 'cancel',
          handler: () => {
            return false;
          },
        },
        {
          text: alertButtonConfirm,
          handler: async (data) => {
            // Check if reason is provided
            if (!data.report_reason || data.report_reason.trim() === '') {
              // Show an alert reminding to fill in the reason field.
              const warningAlert = await this.alertController.create({
                header: alertReasonLabel,
                message: alertReasonPlaceholder,
                buttons: ['OK'],
              });
              await warningAlert.present();
              return false;
            }

            // Check if both checkboxes are checked
            // if (!data.confirmation1 || !data.confirmation2) {
            if (!data.confirmation1) {
              // Show an alert reminding to check both checkboxes
              const warningAlert = await this.alertController.create({
                header: alertConfirmationTitle,
                message: alertConfirmationRequired,
                buttons: ['OK'],
              });
              await warningAlert.present();
              return false;
            }

            let message = '';

            try {
              // Send the report to the backend.
              firstValueFrom(
                this.leaderboard.reportNickname(entry.nickname, entry, data.report_reason, data.confirmation1)
              ).then(async (result) => {
                // We are not showing anything else.
                // if (result.status === 'already_denied') {
                //   message = await firstValueFrom(this.translate.get('PUZZLE.leaderboard.report.already_denied'));
                // } else if (result.status === 'allowed_by_admin') {
                //   message = await firstValueFrom(this.translate.get('PUZZLE.leaderboard.report.allowed_by_admin'));
                // } else {
                if (result.status === 'reported') {
                  message = await firstValueFrom(this.translate.get('PUZZLE.leaderboard.report.success'));
                  void this.puzzleFramework.showTranslatedToast(message, 2500);
                }
              });
            } catch (error) {
              console.error('Error reporting nickname:', error);
              message = await firstValueFrom(this.translate.get('PUZZLE.leaderboard.report.error'));
              void this.puzzleFramework.showTranslatedToast(message, 2500);
            }
            return true;
          },
        },
      ],
    });

    // Show the alert.
    await alert.present();
    // Wait til the alert is closed.
    await alert.onDidDismiss();

    // Re-enable keyboard interception.
    this.keyboardService.enable();
  }
}
